import { ClientOnly } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BellRing, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ensureWechatJssdkConfigured } from "@/lib/client/wechat-jssdk";
import {
  getWechatAuthUrl,
  getWechatBindStatus,
  WECHAT_BIND_STATUS_QUERY_KEY,
} from "@/lib/client/wechat-auth";
import { isWeChatBrowser } from "@/lib/utils/wechat";
import { getWechatReviewSubscriptionStatus, type CurrentUser } from "@/lib/client/user";

function getCurrentPageUrl() {
  if (typeof window === "undefined") {
    return "/";
  }

  return window.location.href.split("#")[0] ?? "/";
}

function getCurrentPathAndSearch() {
  if (typeof window === "undefined") {
    return "/";
  }

  return `${window.location.pathname}${window.location.search}`;
}

function parseSubscribeDetails(raw: string, templateId: string) {
  try {
    const outer = JSON.parse(raw) as Record<string, string>;
    const inner = outer[templateId];
    if (!inner) {
      return null;
    }

    const payload = JSON.parse(inner) as { status?: string };
    return payload.status ?? null;
  } catch {
    return null;
  }
}

function SubscribeOpenTag({ templateId, onAccept }: { templateId: string; onAccept: () => void }) {
  const elementRef = useRef<HTMLElement | null>(null);
  const elementId = useId().replace(/:/g, "-");

  useEffect(() => {
    const controller = new AbortController();
    ensureWechatJssdkConfigured(getCurrentPageUrl(), { signal: controller.signal }).catch(
      (error) => {
        if (controller.signal.aborted) {
          return;
        }

        console.error("微信订阅开放标签初始化失败", error);
      },
    );

    return () => {
      controller.abort("组件卸载");
    };
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    const handleSuccess = (event: Event) => {
      const detail = (event as CustomEvent<{ subscribeDetails?: string }>).detail;
      const status = detail?.subscribeDetails
        ? parseSubscribeDetails(detail.subscribeDetails, templateId)
        : null;

      if (status === "accept") {
        toast.success("订阅成功，后续复盘状态变化会通过微信提醒");
        onAccept();
        return;
      }

      if (status === "reject" || status === "cancel") {
        toast.message("你这次没有完成订阅");
        return;
      }

      if (status === "filter") {
        toast.error("订阅请求被微信过滤，请检查模板配置");
        return;
      }

      toast.message("订阅操作已完成");
    };

    const handleError = (event: Event) => {
      const detail = (event as CustomEvent<{ errMsg?: string }>).detail;
      toast.error(detail?.errMsg || "订阅组件调用失败");
    };

    element.addEventListener("success", handleSuccess as EventListener);
    element.addEventListener("error", handleError as EventListener);

    return () => {
      element.removeEventListener("success", handleSuccess as EventListener);
      element.removeEventListener("error", handleError as EventListener);
    };
  }, [onAccept, templateId]);

  return (
    <wx-open-subscribe
      id={elementId}
      template={templateId}
      style={{
        display: "block",
        width: "100%",
        background: "transparent",
        borderRadius: "12px",
        overflow: "hidden",
        lineHeight: 0,
        padding: 0,
        border: 0,
      }}
      ref={(node: HTMLElement | null) => {
        elementRef.current = node;
      }}
    >
      <script
        type="text/wxtag-template"
        dangerouslySetInnerHTML={{
          __html:
            '<button style="display:flex;align-items:center;justify-content:center;width:100%;padding: 6px 12px;border:1px solid rgba(52,211,153,.36);border-radius:12px;background:linear-gradient(180deg,rgba(15,23,42,.98),rgba(10,18,32,.98));color:#ecfdf5;font-size:14px;font-weight:700;letter-spacing:.02em;box-shadow:inset 0 1px 0 rgba(255,255,255,.05),0 8px 18px rgba(0,0,0,.18);white-space:nowrap;">订阅通知</button>',
        }}
      />
    </wx-open-subscribe>
  );
}

export function WechatReviewSubscriptionCard({
  currentUser,
  context,
  onDismiss,
}: {
  currentUser: CurrentUser | null;
  context: "home" | "review-created";
  onDismiss?: () => void;
}) {
  const inWechatBrowser = isWeChatBrowser();
  const [optimisticSubscribed, setOptimisticSubscribed] = useState(false);

  const { data: bindStatus } = useQuery({
    queryKey: WECHAT_BIND_STATUS_QUERY_KEY,
    queryFn: ({ signal }) => getWechatBindStatus({ signal }),
    enabled: !!currentUser && inWechatBrowser,
  });

  const { data: subscriptionStatus, isLoading } = useQuery({
    queryKey: ["wechat-review-subscription-status"],
    queryFn: () => getWechatReviewSubscriptionStatus(),
    enabled: !!currentUser && inWechatBrowser,
  });

  const syncMutation = useMutation({
    mutationFn: () =>
      getWechatAuthUrl({
        provider: "official-account",
        redirectTo: getCurrentPathAndSearch(),
      }),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "微信身份同步失败");
    },
  });

  const title = useMemo(() => {
    return context === "review-created" ? "申请已提交，建议顺手订阅提醒" : "开启微信复盘状态提醒";
  }, [context]);

  if (!currentUser || !inWechatBrowser || optimisticSubscribed || isLoading) {
    return null;
  }

  if (!bindStatus?.bound || !subscriptionStatus?.enabled || !subscriptionStatus.inWechatBrowser) {
    return null;
  }

  if (subscriptionStatus.subscribed) {
    return null;
  }

  return (
    <Card className="border-emerald-400/18 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.12),transparent_28%),linear-gradient(135deg,rgba(8,16,30,0.96),rgba(10,18,32,0.9))] shadow-[0_16px_40px_rgba(2,6,23,0.28)]">
      <CardContent>
        <div className="flex flex-col gap-5">
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <div className="flex size-8 items-center justify-center rounded-full border border-emerald-400/20 bg-emerald-400/10">
                <BellRing className="size-4 text-emerald-300" />
              </div>
              <p className="text-sm font-semibold tracking-[0.01em] text-emerald-300">{title}</p>
            </div>
            <p className="max-w-2xl text-sm leading-6 text-slate-200/88">
              复盘开始、完成或状态变化后，可以直接在微信里收到提醒。
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/18 bg-slate-950/35 px-2.5 py-1 text-xs text-slate-300">
                <CheckCircle2 className="size-3.5 text-emerald-300" />
                仅需操作一次
              </span>
            </div>
          </div>

          {subscriptionStatus.needsMpOpenIdSync ? (
            <div className="flex flex-col gap-3 rounded-2xl border border-white/6 bg-black/12 p-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm leading-6 text-slate-300/88">
                先完成一次微信授权，之后就可以正常开启提醒了。
              </p>
              <Button
                className="w-full shrink-0 rounded-xl border border-emerald-400/30 bg-emerald-400/12 text-emerald-200 shadow-none hover:bg-emerald-400/18 sm:w-auto"
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
              >
                {syncMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                去开启提醒
              </Button>
            </div>
          ) : subscriptionStatus.templateId ? (
            <ClientOnly>
              <div className="w-full">
                <SubscribeOpenTag
                  templateId={subscriptionStatus.templateId}
                  onAccept={() => {
                    setOptimisticSubscribed(true);
                    onDismiss?.();
                  }}
                />
              </div>
            </ClientOnly>
          ) : null}

          {context === "review-created" && onDismiss ? (
            <Button
              variant="ghost"
              className="w-full text-slate-300 hover:bg-white/5 hover:text-white sm:w-auto sm:self-start"
              onClick={onDismiss}
            >
              稍后再说
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
